import React, { createContext, Dispatch, FC, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import { ConfirmationPriseRendezVous, CreneauxDisponiblesCalendrier, PriseRendezVousData } from '../types/calendrierVisiteModel'
import { formattageHeure, mappingCreneaux } from '../utils/calendrierVisiteUtils'
import { getNextDate, getSpecificDate, getVisiteDate } from '../../../utils/getDays/getNextDate'
import errorCatcher from '../../../utils/errorCatcher'
import { formatageDate } from '../../../utils/formatter/formatDate'
import { PopinConfirmationState } from '../types/popinConfirmation'
import { VisiteConcerneeType } from '../components/VisitesContext'
import { useIntl } from 'react-intl'
import { lotTypesEnum } from '../../../enums/lot/lotTypesEnum'
import { GET_PRISE_RDV } from '../utils/calendrierVisiteConstants'
import { confirmerRDV, priseRDV } from '../api/visitesApi'
import { SelectionPaneContext } from '../../../components/selectionPane/components/SelectionPaneContext'
import { isEmpty } from 'lodash'
import { SnackBarContext } from '../../../components/snackBar/context/SnackBarContext'

export interface CalendrierContextProps {
	creneauxDisponibles: CreneauxDisponiblesCalendrier,
	loadingCreneaux: boolean,
	loadingConfirmation: boolean,
	confirmationRendezVous: () => void,
	date: Date,
	changeDate: Dispatch<SetStateAction<Date>>,
	creneau: string | null,
	setCreneau: Dispatch<SetStateAction<string | null>>,
	visiteParking: boolean,
	setVisiteParking: Dispatch<SetStateAction<boolean>>,
	minDate: Date,
	maxDate: Date
}

export const CalendrierContext = createContext<CalendrierContextProps>({
	creneauxDisponibles: {},
	loadingCreneaux: true,
	loadingConfirmation: false,
	confirmationRendezVous: () => {
	},
	date: new Date(),
	changeDate: (date) => {
	},
	creneau: null,
	setCreneau: (creneau) => {
	},
	visiteParking: false,
	setVisiteParking: (visiteParking) => {
	},
	minDate: new Date(),
	maxDate: new Date()
})

interface AcceptingProps {
	offre: VisiteConcerneeType,
	setPopinConfirmation: (popinConfirmation: PopinConfirmationState) => void,
	popinErreurConfirmation: boolean,
	setPopinErreurConfirmation: (popinErreurConfirmation: boolean) => void
	children: ReactNode
}

export const CalendrierProvider: FC<AcceptingProps> = (
	{
		children,
		offre,
		setPopinConfirmation,
		popinErreurConfirmation,
		setPopinErreurConfirmation
	}
) => {
	const intl = useIntl()
	const { displaySnackBar } = useContext(SnackBarContext)
	const [creneauxDisponibles, setCreneauxDisponibles] = useState<CreneauxDisponiblesCalendrier>({})
	const [loadingCreneaux, setLoadingCreneaux] = useState(true)
	const [loadingConfirmation, setLoadingConfirmation] = useState(false)
	const [date, changeDate] = useState(new Date(getNextDate()))
	const [creneau, setCreneau] = useState<string | null>(null)
	const [minDate, setMinDate] = useState(new Date(getNextDate()))
	const [maxDate, setMaxDate] = useState(new Date(getSpecificDate(16)))
	const [visiteParking, setVisiteParking] = useState<boolean>(!!offre.souhaitVisiteParking)
	const { currentClient, demandeResidentielId } = useContext(SelectionPaneContext)
	const CONFIRMATION_PRISE_RDV = 'confirmationPriseRDV'

	useEffect(() => {
		if (!popinErreurConfirmation) {
			setLoadingCreneaux(true)
			let dateMinimale = new Date(getNextDate())
			let data: PriseRendezVousData = {
				id_residence: offre.residence.identifiantCRM,
				id_lot: offre.idLot,
				duree_rdv: offre.type === lotTypesEnum.apartment ? offre.residence.dureeVisite : 30,
				id_offre: offre.identifiantCRMOffre,
				demande_resi: demandeResidentielId ?? ''
			}

			if (!!currentClient && !isEmpty(currentClient)) {
				data = { ...data, crm_id: currentClient.id }
			}

			priseRDV(data)
				.then((response: any) => {
					const creneaux = response.creneaux
					const creneauxDisponiblesTmp = mappingCreneaux(creneaux)

					// Si l'appartement est sous congé, le jour courant est jour + 2
					if (response.appartementSousConge) {
						changeDate(new Date(getSpecificDate(2)))
						setMinDate(new Date(getSpecificDate(2)))
						setMaxDate(new Date(getSpecificDate(17)))
						dateMinimale = new Date(getSpecificDate(2))
					}

					// Si "lotVisitable a partir de" est rempli, et après la date minimale actuelle, on le prend en compte
					if (response.lotVisitableAPartirDe) {
						const dateVisitable = new Date(response.lotVisitableAPartirDe)
						if (dateVisitable.getTime() > dateMinimale.getTime()) {
							changeDate(dateVisitable)
							setMinDate(dateVisitable)
							setMaxDate(new Date(getVisiteDate(dateVisitable, 15)))
						}
					}

					setCreneauxDisponibles(creneauxDisponiblesTmp)
				})
				.catch(() => displaySnackBar(errorCatcher(intl.formatMessage({ id: 'visites.popin.priseRDV.calendrier.erreur' }), GET_PRISE_RDV)))
				.finally(() => setLoadingCreneaux(false))
		}
	}, [popinErreurConfirmation, displaySnackBar, intl, offre, currentClient, demandeResidentielId])

	const confirmationRendezVous = useCallback(() => {
		const dateFormatee = formatageDate(date.toString(), 'yyyy-MM-dd')

		let data: ConfirmationPriseRendezVous = {
			// On met un '!' car le créneau est forcément présent
			date_rdv: `${dateFormatee} ${formattageHeure(creneau!)}`,
			demande_resi: demandeResidentielId ?? '',
			duree_rdv: offre.type === lotTypesEnum.apartment ? offre.residence.dureeVisite : 30,
			offre: offre.identifiantCRMOffre ?? '',
			genre: offre.genre,
			reference: offre.referenceCRM ?? '',
			lastname: currentClient?.nom || '',
			phone: currentClient?.telephone || '',
			visiteId: offre.visiteId ?? null,
			idLot: offre.idLot,
			visite_parking: visiteParking,
			fromSitel: true,
			id_residence: offre.residence.identifiantCRM
		}

		if (!!currentClient && !isEmpty(currentClient)) {
			data = { ...data, crm_id: currentClient.id }
		}

		setLoadingConfirmation(true)

		confirmerRDV(data)
			.then(
				() => setPopinConfirmation({
					open: true,
					date: `${dateFormatee} ${formattageHeure(creneau!)}`
				})
			)
			.catch((error) => {
				if (typeof error === 'string') {
					displaySnackBar({
						id: CONFIRMATION_PRISE_RDV,
						open: true,
						hideIcon: true,
						message: intl.formatMessage({ id: error })
					})
				}
				setCreneau(null)
				setPopinErreurConfirmation(true)
			})
			.finally(() => setLoadingConfirmation(false))
	}, [date, demandeResidentielId, displaySnackBar, intl, creneau, offre, setPopinErreurConfirmation, setPopinConfirmation, visiteParking, currentClient])

	return (
		<CalendrierContext.Provider
			value={{
				creneauxDisponibles,
				loadingCreneaux,
				loadingConfirmation,
				confirmationRendezVous,
				date,
				changeDate,
				creneau,
				setCreneau,
				minDate,
				maxDate,
				visiteParking,
				setVisiteParking
			}}
		>
			{children}
		</CalendrierContext.Provider>
	)
}